import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { CommercialAccessState, CompanyModule, CompanyModules, ModulePreset, UpdateCompanyModuleInput } from '@floorfile/contracts'
import { isInfrastructureFailure } from '@floorfile/offline'
import { useAuth } from './auth'
import { useEventSubscription } from './event-feed'

interface ModulesState {
  status: 'loading' | 'ready' | 'error'
  preset: ModulePreset
  modules: readonly CompanyModule[]
  enabled: ReadonlySet<string>
  isEnabled: (moduleKey: string) => boolean
  refresh: () => Promise<void>
  /** The commercial account's state (Platform Console plan §10) — the tenant
   * learns THAT it is restricted or in grace, nothing about plan or price. */
  accountAccess: CommercialAccessState
  update: (moduleKey: string, input: UpdateCompanyModuleInput) => Promise<void>
  /** Resolves with the server's answer, which names the preset's modules the
   * subscription does not include (`unavailableForPreset`). */
  applyPreset: (preset: Exclude<ModulePreset, 'CUSTOM'>) => Promise<CompanyModules>
}

const ModulesContext = createContext<ModulesState | undefined>(undefined)

/**
 * Best-effort cache of the company's enabled-module list, per company, so a
 * device that cannot reach the server still knows which modules exist
 * (owner decision, 2026-08-03).
 *
 * ## The bug this fixes
 *
 * The list arrived from one `fetch` held in `useState` with nothing behind it.
 * Offline the request throws, the status became `error`, the enabled set stayed
 * EMPTY, and `AppShell` reads an empty set as "your company does not have this
 * module" — so an installed tablet in a dead zone lost Quality and Maintenance
 * from the sidebar and answered `/checks` with "Module unavailable". Every one
 * of the eight offline-first screens (`vite.config.ts`) was unreachable in
 * exactly the situation they exist for. Audit RUN-11 fixed the worse half of
 * this (the status used to stay `loading` forever); what remained was that an
 * UNKNOWN module list was being treated as a DISABLED one.
 *
 * ## Why it is safe
 *
 * A module toggle is a licensing/packaging switch, not a permission. The API
 * rejects a call into a disabled module with `403 FEATURE_DISABLED` regardless
 * of what this cache says, and every screen behind one is permission-gated
 * separately. The exposure of a stale list is therefore a screen that opens and
 * then errors — never data from a module the company has switched off.
 *
 * The accepted cost, stated plainly: a module disabled while a tablet is
 * offline stays visible on that tablet until it next reaches the server.
 *
 * ## Shape
 *
 * Keyed by company, not by user — the list is company configuration, and a
 * shared floor tablet swaps users constantly. It deliberately survives sign-out
 * for the same reason `requireOfflineEncryption` in `lib/auth.tsx` does: the
 * offline sign-in that needs it happens BEFORE any fetch can succeed. It is not
 * per-user data, so it is not part of `clearUserScopedStorage`.
 */
const MODULES_CACHE_PREFIX = 'floorfile.modules:'

export function readCachedModules(companyId: string | undefined): CompanyModules | undefined {
  if (!companyId) return undefined
  try {
    const raw = localStorage.getItem(MODULES_CACHE_PREFIX + companyId)
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as CompanyModules
    // Shape-checked rather than trusted: this is data a previous BUILD wrote,
    // and a half-parsed payload would silently disable everything, which is the
    // failure being fixed here.
    if (!Array.isArray(parsed?.modules)) return undefined
    return parsed
  } catch {
    return undefined
  }
}

export function writeCachedModules(companyId: string | undefined, value: CompanyModules): void {
  if (!companyId) return
  try {
    localStorage.setItem(MODULES_CACHE_PREFIX + companyId, JSON.stringify(value))
  } catch {
    /* storage unavailable (private mode, quota) — this session still works */
  }
}

export function ModulesProvider({ children }: { children: ReactNode }) {
  const { authFetch, companyId, status: authStatus } = useAuth()
  const [status, setStatus] = useState<ModulesState['status']>('loading')
  const [payload, setPayload] = useState<CompanyModules>({ preset: 'CUSTOM', accountAccess: 'ACTIVE', modules: [] })

  const apply = useCallback((next: CompanyModules) => {
    setPayload(next)
    setStatus('ready')
    writeCachedModules(companyId, next)
  }, [companyId])

  const refresh = useCallback(async () => {
    if (authStatus !== 'authenticated') return
    // The try/catch is the point (audit RUN-11). `!response.ok` was handled, but
    // a THROWN authFetch (network failure, CORS, abort) bypassed it entirely:
    // the rejection escaped into `void refresh()` and `status` stayed
    // 'loading' forever. App.tsx renders a bare "Loading…" while that is true,
    // so every module-gated route (~25, via RequireModule) was permanently
    // wedged after one offline moment — on a PWA whose users routinely lose
    // connectivity.
    try {
      const response = await authFetch('/api/v1/settings/modules')
      const body = (await response.json().catch(() => undefined)) as { data?: CompanyModules } | undefined
      // FF-AUDIT-088: a dead proxy or edge answers 5xx WITHOUT a FloorFile
      // envelope — that is an unreachable server in another costume and falls
      // through to the cache like a thrown fetch would.
      if (!response.ok && isInfrastructureFailure(response.status, body)) throw new Error(`unreachable (${response.status})`)
      if (!response.ok || !body?.data) {
        // A STATUS CODE IS AN ANSWER. The server was reached and said no —
        // 401, 403, an application 500 — so the cache must not paper over it.
        // Only an unreachable server (the catch below) means "we do not know",
        // and only that case is allowed to fall back to what we last saw.
        setStatus('error')
        return
      }
      apply(body.data)
    } catch {
      const cached = readCachedModules(companyId)
      if (cached) {
        // Deliberately `ready`, not a third state. Every consumer asks one
        // question — "is this module on?" — and the honest answer offline is
        // the last one the server gave. A `isStale` flag was considered and
        // left out: nothing renders it today, and a flag nobody reads is a
        // reviewer's question rather than a feature.
        setPayload(cached)
        setStatus('ready')
        return
      }
      setStatus('error')
    }
  }, [apply, authFetch, authStatus, companyId])

  useEffect(() => {
    if (authStatus === 'authenticated') void refresh()
    else {
      setPayload({ preset: 'CUSTOM', accountAccess: 'ACTIVE', modules: [] })
      setStatus('loading')
    }
  }, [authStatus, refresh])

  useEventSubscription(['settings.updated'], () => void refresh())

  const update = useCallback(async (moduleKey: string, input: UpdateCompanyModuleInput) => {
    const response = await authFetch(`/api/v1/settings/modules/${encodeURIComponent(moduleKey)}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    })
    const body = (await response.json()) as { data?: CompanyModules; error?: { code?: string; message: string } }
    if (!response.ok || !body.data) throw envelopeError(body.error, 'Could not update the module.')
    apply(body.data)
  }, [apply, authFetch])

  const applyPreset = useCallback(async (preset: Exclude<ModulePreset, 'CUSTOM'>) => {
    const response = await authFetch('/api/v1/settings/modules/apply-preset', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ preset }),
    })
    const body = (await response.json()) as { data?: CompanyModules; error?: { code?: string; message: string } }
    if (!response.ok || !body.data) throw envelopeError(body.error, 'Could not apply the preset.')
    apply(body.data)
    return body.data
  }, [apply, authFetch])

  const enabled = useMemo(() => new Set(payload.modules.filter((module) => module.enabled).map((module) => module.moduleKey)), [payload.modules])
  const value = useMemo<ModulesState>(() => ({
    status,
    preset: payload.preset,
    modules: payload.modules,
    enabled,
    // A list cached by a build before the commercial fields existed has no
    // `accountAccess`; it was written when access was unrestricted.
    accountAccess: payload.accountAccess ?? 'ACTIVE',
    isEnabled: (moduleKey) => enabled.has(moduleKey),
    refresh,
    update,
    applyPreset,
  }), [status, payload, enabled, refresh, update, applyPreset])

  return <ModulesContext.Provider value={value}>{children}</ModulesContext.Provider>
}

/**
 * An envelope error keeps its CODE, so the screen translates it
 * (`useApiErrorMessage`) instead of printing the server's English — the
 * entitlement refusals (`MODULE_NOT_ENTITLED`, `ACCOUNT_RESTRICTED`…) are
 * exactly the ones a tenant reads.
 */
function envelopeError(error: { code?: string; message: string } | undefined, fallback: string): Error {
  return Object.assign(new Error(error?.message ?? fallback), error?.code ? { code: error.code } : {})
}

export function useModules(): ModulesState {
  const value = useContext(ModulesContext)
  if (!value) throw new Error('useModules must be used inside <ModulesProvider>')
  return value
}
